import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Phone, ArrowRight, MapPin } from "lucide-react";
import { BRAND } from "../lib/api";
import LeadForm from "./LeadForm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBob21lfGVufDB8fHx8MTc4NTI0NTQ1Mnww&ixlib=rb-4.1.0&q=85";

const LINES = ["GREAT", "QUALITY.", "GREAT", "EXPERIENCE."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.12, duration: 0.9, ease: [0.6, 0.05, 0.15, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const [formOpen, setFormOpen] = useState(false);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full pt-28 md:pt-32 pb-16 overflow-hidden"
    >
      {/* Parallax spotlight image, right side */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full md:w-[55%] pointer-events-none"
      >
        <div className="relative h-full w-full">
          <img
            src={HERO_IMG}
            alt="Freshly painted modern home exterior in Montgomery, AL"
            fetchpriority="high"
            decoding="async"
            className="h-full w-full object-cover grayscale opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-bg via-brand-bg/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-transparent to-transparent" />
        </div>
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* LEFT: title + CTA */}
        <div className="lg:col-span-7 pt-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-brand-muted mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-8 bg-brand-blue" />
            <span>Est. Central Alabama · Residential & Commercial</span>
          </motion.div>

          <h1 className="sr-only">
            Professional Interior Painters in Montgomery, AL — RRR Paint &amp; Construction LLC. Best residential exterior painting services, commercial painting, and construction serving Montgomery, Wetumpka, Prattville, and Central Alabama. Free same-day quotes. Call 334-201-9444.
          </h1>

          <div
            role="heading"
            aria-level="1"
            aria-hidden="true"
            className="font-display leading-[0.82] tracking-tight text-white text-[18vw] sm:text-[14vw] lg:text-[9.5vw] uppercase"
          >
            {LINES.map((word, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={lineVariants}
                >
                  {word === "QUALITY." || word === "EXPERIENCE." ? (
                    <span className="text-brand-blue">{word}</span>
                  ) : (
                    word
                  )}
                </motion.span>
              </span>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-xl text-brand-muted text-base md:text-lg font-sans"
            data-testid="hero-subtitle"
          >
            Painting & construction done right — interior, exterior, and everything in
            between. Call now for a <span className="text-white font-semibold">FREE same-day quote.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <a
              href={BRAND.phoneHref}
              data-testid="hero-call-btn"
              className="group inline-flex items-center gap-3 bg-brand-blue hover:bg-brand-blueHover text-white px-7 py-5 font-display tracking-wider text-2xl uppercase transition-all duration-300 hover:pl-9"
            >
              <Phone className="h-5 w-5" strokeWidth={2.5} />
              <span>{BRAND.phone}</span>
              <ArrowRight className="h-5 w-5 opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </a>
            <button
              onClick={() => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-3 border border-white/30 hover:border-white text-white px-7 py-5 font-display tracking-wider text-2xl uppercase transition-colors"
            >
              Get Free Quote
              <ArrowRight className="h-5 w-5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="mt-8 flex items-center gap-2 text-brand-muted text-sm"
          >
            <MapPin className="h-4 w-4 text-brand-blue" />
            {BRAND.area}
          </motion.div>
        </div>

        {/* RIGHT: lead form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8, ease: [0.6, 0.05, 0.15, 1] }}
          className="lg:col-span-5 w-full"
          id="lead-form"
        >
          <LeadForm />
        </motion.div>
      </div>
    </section>
  );
}
